import { Box, Typography } from "@mui/material";
import React, { useMemo, useState } from "react";
import { TranslateFunction, createTranslateFn } from "../../utils/formUtils";
import { getTranslatedFieldByLocale } from "../../utils/translatedFields";
import { useI18n } from "../hooks/useI18n";
import { useVaccineWithDiseases } from "../hooks/useVaccineWithDiseases";
import { VaccineWithDiseases, useVaccinesWithDiseases } from "../hooks/useVaccinesWithDiseases";
import { StyledAutocomplete } from "./mui/StyledAutoComplete";

const FormVaccinesAutocomplete = ({
  readOnly = false,
  vaccineId,
  performedOn,
  onVaccineChange,
  error,
  touched,
}: {
  readOnly?: boolean;
  vaccineId?: string;
  performedOn: Date | null;
  onVaccineChange?: (vaccineId: string) => void;
  error?: string;
  touched?: boolean;
}) => {
  const { t, locale } = useI18n();
  const [vaccineInput, setVaccineInput] = useState<string>("");
  const [isTouched, setIsTouched] = useState<boolean>(touched || false);
  const translateErrors: TranslateFunction = useMemo(() => createTranslateFn(t), [t]);

  const { vaccinesWithDiseases, isLoading: isLoadingVaccinesWithDiseases } = useVaccinesWithDiseases(
    vaccineInput,
    performedOn ?? undefined,
  );

  const vaccineWithDisease = useVaccineWithDiseases(vaccineId);

  function renderVaccine(vaccineWithDiseases: VaccineWithDiseases) {
    const vaccine = vaccineWithDiseases.vaccine;
    return (
      <Box>
        <Typography fontWeight="bold" fontSize="0.9rem">
          {getTranslatedFieldByLocale(locale, vaccine.name)}
        </Typography>
        <Typography fontSize="0.8rem">
          {vaccineWithDiseases.diseases.map((d) => getTranslatedFieldByLocale(locale, d.name)).join(", ")}
        </Typography>
        {vaccineWithDiseases.distributed && vaccineWithDiseases.distribution?.startsOn && (
          <Typography fontSize="0.75rem">
            {vaccineWithDiseases.distribution?.endsOn
              ? t("vaccines.distributedBetween", {
                  distributionStart: vaccineWithDiseases.distribution.startsOn.toLocaleDateString(
                    locale === "en-gb" ? "en" : locale,
                  ),
                  distributionEnd: vaccineWithDiseases.distribution.endsOn.toLocaleDateString(
                    locale === "en-gb" ? "en" : locale,
                  ),
                })
              : t("vaccines.distributedSince", {
                  distributionStart: vaccineWithDiseases.distribution.startsOn.toLocaleDateString(
                    locale === "en-gb" ? "en" : locale,
                  ),
                })}
          </Typography>
        )}
      </Box>
    );
  }

  const handleVaccineChange = (
    _event: React.SyntheticEvent<Element, Event>,
    newVaccineWithDiseases: VaccineWithDiseases | null,
  ) => {
    setIsTouched(true);
    const newVaccineId = newVaccineWithDiseases?.vaccine?.id ?? "";
    onVaccineChange?.(newVaccineId);
  };

  return (
    <StyledAutocomplete
      id="vaccineId"
      noOptionsText={t("common.errors.noResults")}
      error={isTouched && !!error}
      errorMessage={error}
      touched={isTouched}
      translateErrors={translateErrors}
      fullWidth
      label={t("vaccines.name_or_disease")}
      placeholder={t("vaccines.placeholder")}
      required
      loading={isLoadingVaccinesWithDiseases}
      options={vaccinesWithDiseases}
      readOnly={readOnly}
      getOptionLabel={(vaccineWithDiseases) => vaccineWithDiseases.vaccineNameWithDiseases}
      groupBy={(option) => (option.distributed ? t("vaccines.vaccinesDistributed") : t("vaccines.otherVaccines"))}
      filterOptions={(options) => options}
      renderGroup={(params) => {
        return (
          <li key={params.key}>
            <Box
              sx={{
                position: "sticky",
                top: "-8px",
                padding: "8px 10px",
                color: "rgba(0,0,0,0.6)",
                fontSize: "14px",
                backgroundColor: "white",
              }}
            >
              {params.group}
            </Box>
            <Box component="ul" sx={{ p: 0 }}>
              {params.children}
            </Box>
          </li>
        );
      }}
      renderOption={(props, vaccineWithDisease) => (
        <li {...props} key={vaccineWithDisease.vaccine.id}>
          {renderVaccine(vaccineWithDisease)}
        </li>
      )}
      value={vaccineWithDisease ?? null}
      onInputChange={(_e, value) => setVaccineInput(value)}
      onChange={handleVaccineChange}
    />
  );
};

export default FormVaccinesAutocomplete;
